import PropTypes from "prop-types";
import React from "react";
import { withTranslation } from "react-i18next";

import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import { withStyles, withTheme } from "@material-ui/core/styles";
import BuildIcon from "@material-ui/icons/Build";
import CloseIcon from "@material-ui/icons/Close";

import Typography from "../../../../components/Helpers/Typography";
import SPScrollbar from "../../../../components/Navigation/SPScrollbar";
import useMediaQuery from "../../../../hooks/useMediaQuery";
import { DateService } from "../../../../services/dates.service";
// eslint-disable-next-line import/no-cycle
import EventItem from "./EventItem";

const styles = (theme) => ({
  dialog: {
    color: theme.palette.text.primary,
  },
  dialogIcon: {
    color: theme.palette.text.primary,
    height: 40,
    width: 40,
    margin: 10,
  },
  formattedEvent: {
    paddingBottom: 14,
    marginBottom: 0,
    fontFamily: "Roboto",
    backgroundColor: theme.palette.primary.main,
  },
  container: {
    margin: "0",
    width: "100%",
    paddingRight: 0,
    paddingTop: 12,
    paddingBottom: 12,
  },
  blueButton: {
    color: theme.palette.text.blue,
    background: "none",
    border: "none",
    textTransform: "uppercase",
    cursor: "pointer",
  },
  lastRefreshed: {
    background: "#08090D",
    paddingTop: 16,
    paddingBottom: 16,
    display: "flex",
    justifyContent: "flex-end",
  },
});

const formatValue = (value = "") => {
  if (typeof value !== "function") {
    return JSON.stringify(value, <br />, 4).replace(/[{}["\]]/g, "");
  }
  return "";
};

// Replaced by DiagnosticModalContext but still here for timeline work
export const ModalContext = React.createContext({
  setDiagnostics: () => () => {},
  handleModalClose: () => {},
});

export const DiagnosticTable = (props) => {
  const { diagnostics } = props;

  const renderDiagnosticsTable = () =>
    Object.entries(diagnostics).map(([key, value]) => {
      // don't show functions in the diagnostic
      if (typeof value !== "function") {
        return (
          <Grid container key={key}>
            <Grid item xs={1} md={false} />
            <Grid item xs={11}>
              <Typography
                color="lightgrey"
                style={{
                  fontSize: 10,
                }}
              >
                <span style={{ textTransform: "capitalize" }}>{key.toString()}:</span>{" "}
                {formatValue(value)}
              </Typography>
            </Grid>
          </Grid>
        );
      }
      return null;
    });

  return (
    <pre
      style={{
        padding: 14,
        marginBottom: 0,
        fontFamily: "Roboto",
        backgroundColor: "#1A1D28",
        marginTop: 0,
      }}
    >
      {renderDiagnosticsTable(diagnostics)}
    </pre>
  );
};

DiagnosticTable.propTypes = {
  diagnostics: PropTypes.object.isRequired,
};

export const DiagnosticModal = (props) => {
  const {
    modalOpen,
    handleModalClose,
    classes,
    t,
    modalId,
    sortedDiagnostics,
    diagnosticsTimestamp,
    refreshFunction,
    handleModalToggle,
    theme,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const renderDiagnostics = (diagnostics) => {
    if (diagnostics.length === 0) {
      const typographyStyle = {
        textAlign: "center",
        width: "100%",
      };
      return (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Typography style={typographyStyle}>
            {t("Vehicles.Detail.Modals.LogsUnavailable")}
          </Typography>
          <br />
          <Typography style={typographyStyle} color="secondary">
            {t("Vehicles.Detail.Modals.LogsUnavailableDetails1")}
          </Typography>
          <Typography style={typographyStyle} color="secondary">
            {t("Vehicles.Detail.Modals.LogsUnavailableDetails2")}
          </Typography>
        </div>
      );
    }
    return diagnostics.map((event, index) => (
      <EventItem
        key={event.key}
        event={event}
        index={index}
        length={sortedDiagnostics?.length}
      />
    ));
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleModalClose("DiagnosticModal")}
      aria-labelledby="diagnostic-modal"
      fullWidth
      maxWidth="md"
      classes={{ paper: classes.dialog }}
      id="something-dialog-something1"
      fullScreen={isMobile}
      style={{ zIndex: 9010 }}
    >
      <DialogTitle>
        <Grid container>
          <Grid item xs={10}>
            <Typography
              style={{
                fontWeight: 500,
                fontSize: 20,
                height: "100%",
              }}
              color="lightgrey"
            >
              {t("Diagnostics")}
            </Typography>
          </Grid>
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              height: 60,
              width: 200,
              background: "transparent",
              display: "inline-flex",
              justifyContent: "flex-end",
            }}
          >
            {handleModalToggle && (
              <IconButton onClick={handleModalToggle} className={classes.dialogIcon}>
                <BuildIcon />
              </IconButton>
            )}

            <IconButton
              onClick={handleModalClose(modalId)}
              className={classes.dialogIcon}
              data-testid="diagnostic_modal_close_icon"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </Grid>
      </DialogTitle>
      <DialogContent
        style={{
          backgroundColor: theme.palette.background.dark,
          padding: 0,
          overflow: "hidden",
        }}
      >
        <div className={classes.lastRefreshed}>
          <Typography
            style={{ fontSize: 14, textAlign: "right", marginRight: 20 }}
            color="lightgrey"
          >
            {t("Last updated")} {DateService.format(diagnosticsTimestamp, "hh:mm:ss")}
          </Typography>
          <button type="button" onClick={() => refreshFunction()} className={classes.blueButton}>
            <Typography color="blue">{t("Refresh")}</Typography>
          </button>
        </div>

        <SPScrollbar
          style={{
            height: isMobile ? "calc(100vh - 130px)" : 500,
          }}
        >
          {renderDiagnostics(sortedDiagnostics)}
        </SPScrollbar>
      </DialogContent>
    </Dialog>
  );
};

DiagnosticModal.propTypes = {
  t: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  modalId: PropTypes.string,
  modalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  handleModalToggle: PropTypes.func,
  sortedDiagnostics: PropTypes.array.isRequired,
  diagnosticsTimestamp: PropTypes.string,
  refreshFunction: PropTypes.func,
};

DiagnosticModal.defaultProps = {
  modalId: "DiagnosticModal",
  handleModalToggle: null,
  diagnosticsTimestamp: "",
  refreshFunction: () => {},
};

export default withStyles(styles)(withTheme(withTranslation("common")(DiagnosticModal)));
